import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Crop, UserRound } from 'lucide-react';
import AuthorAvatar from '../common/AuthorAvatar';
import CircleCropModal from '../common/CircleCropModal';
import { footerApi } from '../../utils/api';

function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [photoFiles, setPhotoFiles] = useState({}); // index -> File (pending uploads)
  const [previews, setPreviews] = useState({}); // index -> objectURL
  const [cropState, setCropState] = useState(null); // { index, src } — 'default' = default author photo
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Default author — samachar ma lekhak nadiyema prayog hune (photo sahit)
  const [defAuthor, setDefAuthor] = useState({ name: '', photo: '' });
  const [defPhotoFile, setDefPhotoFile] = useState(null);
  const [defPreview, setDefPreview] = useState(null);
  const [defSaving, setDefSaving] = useState(false);

  useEffect(() => {
    fetchTeam();
    return () => Object.values(previews).forEach((u) => u && URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const response = await footerApi.getSettings();
      try {
        const arr = JSON.parse(response.data?.team_members || '[]');
        setTeam(Array.isArray(arr) ? arr : []);
      } catch {
        setTeam([]);
      }
      try {
        const d = typeof response.data?.default_author === 'string'
          ? JSON.parse(response.data.default_author || '{}')
          : response.data?.default_author;
        if (d && typeof d === 'object') {
          setDefAuthor((prev) => ({ name: prev.name || d.name || '', photo: d.photo || '' }));
        }
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTeamMember = (i, field, value) => {
    setTeam(team.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));
  };

  const addTeamMember = () => setTeam([...team, { name: '', role: '', phone: '', email: '', photo: '' }]);

  const removeTeamMember = (i) => {
    setCropState(null);
    setTeam(team.filter((_, idx) => idx !== i));
    setPhotoFiles((pf) => {
      const next = {};
      Object.entries(pf).forEach(([k, v]) => {
        const idx = Number(k);
        if (idx < i) next[idx] = v;
        else if (idx > i) next[idx - 1] = v;
      });
      return next;
    });
    setPreviews((pv) => {
      if (pv[i]) URL.revokeObjectURL(pv[i]);
      const next = {};
      Object.entries(pv).forEach(([k, v]) => {
        const idx = Number(k);
        if (idx === i) return;
        next[idx < i ? idx : idx - 1] = v;
      });
      return next;
    });
  };

  const handlePhotoChange = (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // same file feri select garna sakne
    const reader = new FileReader();
    reader.onload = (event) => setCropState({ index: i, src: event.target.result });
    reader.readAsDataURL(file);
  };

  // Crop modal bata cropped File aayepachhi
  const handleCropApply = (file) => {
    const { index } = cropState || {};
    if (index === null || index === undefined) return;
    if (index === 'default') {
      setDefPhotoFile(file);
      if (defPreview) URL.revokeObjectURL(defPreview);
      setDefPreview(URL.createObjectURL(file));
      setCropState(null);
      return;
    }
    setPhotoFiles((pf) => ({ ...pf, [index]: file }));
    setPreviews((pv) => {
      if (pv[index]) URL.revokeObjectURL(pv[index]);
      return { ...pv, [index]: URL.createObjectURL(file) };
    });
    setCropState(null);
  };

  // ---- Default author ----
  const handleDefaultPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (event) => setCropState({ index: 'default', src: event.target.result });
    reader.readAsDataURL(file);
  };

  const submitDefaultAuthor = async () => {
    if (!String(defAuthor.name || '').trim()) {
      toast.error('पूर्वनिर्धारित लेखकको नाम आवश्यक छ।');
      return;
    }
    setDefSaving(true);
    try {
      const payload = {
        default_author: JSON.stringify({ name: defAuthor.name.trim(), photo: defAuthor.photo || '' }),
      };
      await footerApi.updateSettings(payload, defPhotoFile ? { defaultAuthorPhoto: [defPhotoFile] } : undefined);
      toast.success('पूर्वनिर्धारित लेखक सुरक्षित भयो।');
      setDefPhotoFile(null);
      if (defPreview) URL.revokeObjectURL(defPreview);
      setDefPreview(null);
      await fetchTeam();
    } catch (error) {
      console.error('Error updating default author:', error);
      toast.error('सुरक्षित गर्न असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    } finally {
      setDefSaving(false);
    }
  };

  const handleSubmit = async () => {
    const cleaned = team.filter((m) => m && String(m.name || '').trim());
    if (cleaned.length !== team.length) {
      // Re-map pending files after removing empty-name rows
      const nameKeptIdx = [];
      team.forEach((m, i) => {
        if (m && String(m.name || '').trim()) nameKeptIdx.push(i);
      });
      const remappedFiles = {};
      const remappedPreviews = {};
      nameKeptIdx.forEach((oldIdx, newIdx) => {
        if (photoFiles[oldIdx]) remappedFiles[newIdx] = photoFiles[oldIdx];
        if (previews[oldIdx]) remappedPreviews[newIdx] = previews[oldIdx];
      });
      setPhotoFiles(remappedFiles);
      setPreviews(remappedPreviews);
    }

    const files = [];
    const indexes = [];
    cleaned.forEach((_, i) => {
      if (photoFiles[i] instanceof File) {
        files.push(photoFiles[i]);
        indexes.push(i);
      }
    });

    setSaving(true);
    try {
      const payload = { team_members: JSON.stringify(cleaned), team_photo_indexes: indexes };
      await footerApi.updateSettings(payload, { teamPhotos: files });
      toast.success('टिम सदस्यहरू सफलतापूर्वक अद्यावधिक गरियो।');
      Object.values(previews).forEach((u) => u && URL.revokeObjectURL(u));
      setPhotoFiles({});
      setPreviews({});
      await fetchTeam();
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('अद्यावधिक गर्न असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft';
  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">हाम्रो टिम व्यवस्थापन</h2>
        <button className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark disabled:opacity-60" onClick={handleSubmit} disabled={saving}>
          <Save size={15} /> {saving ? 'सुरक्षित गर्दै...' : 'सुरक्षित गर्नुहोस्'}
        </button>
      </div>

      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6 mb-5">
        <div className="flex items-center gap-3 mb-1 pb-4 border-b border-line">
          <span className="w-10 h-10 rounded-xl bg-crimson/10 text-crimson flex items-center justify-center flex-shrink-0">
            <UserRound size={19} />
          </span>
          <div className="flex-1">
            <h3 className="font-display text-[17px] font-bold text-ink leading-tight">पूर्वनिर्धारित लेखक</h3>
            <p className="text-[12px] text-muted">
              समाचार थप्दा लेखक छानिएको छैन भने यही लेखक (फोटोसहित) स्वतः प्रयोग हुन्छ।
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mt-4">
          <label className={labelCls}>
            फोटो
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="relative group">
                {defPreview || defAuthor.photo ? (
                  <>
                    <img
                      src={defPreview || defAuthor.photo}
                      alt={defAuthor.name || 'Default author'}
                      className="w-16 h-16 rounded-full object-cover border-2 border-line bg-paper-soft"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCropState({ index: 'default', src: defPreview || defAuthor.photo });
                      }}
                      title="फोटो क्रप गर्नुहोस्"
                      aria-label="फोटो क्रप गर्नुहोस्"
                      className="absolute inset-x-0 -bottom-0.5 mx-auto w-fit inline-flex items-center gap-1 bg-crimson text-white text-[10px] font-bold px-2 py-0.5 rounded-full opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <Crop size={10} /> क्रप
                    </button>
                  </>
                ) : (
                  <AuthorAvatar name={defAuthor.name || '?'} size={64} />
                )}
              </div>
              <input type="file" accept="image/*" onChange={handleDefaultPhotoChange} className={`${inputCls} py-2 max-w-[220px]`} />
              {(defPreview || defPhotoFile) && (
                <button
                  type="button"
                  className="text-[12.5px] font-semibold text-crimson hover:underline"
                  onClick={() => {
                    if (defPreview) URL.revokeObjectURL(defPreview);
                    setDefPreview(null);
                    setDefPhotoFile(null);
                  }}
                >
                  नयाँ फोटो रद्द
                </button>
              )}
            </div>
          </label>
          <label className={labelCls}>
            नाम
            <input
              className={inputCls}
              value={defAuthor.name || ''}
              onChange={(e) => setDefAuthor((d) => ({ ...d, name: e.target.value }))}
              placeholder="कालो न्युज"
            />
            <span className="text-[11px] text-muted font-normal">
              यो फोटो/नाम समाचारमा लेखक खाली छोड़िएको बेला मात्र देखिन्छ।
            </span>
          </label>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submitDefaultAuthor}
            disabled={defSaving}
            className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13px] px-4 py-2 rounded-full hover:bg-crimson-dark disabled:opacity-60"
          >
            <Save size={14} /> {defSaving ? 'सुरक्षित गर्दै...' : 'पूर्वनिर्धारित लेखक सुरक्षित गर्नुहोस्'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="font-display text-lg">टिम सदस्यहरू</h3>
          <button type="button" onClick={addTeamMember} className="inline-flex items-center gap-1.5 bg-crimson/10 text-crimson font-bold text-[13px] px-4 py-2 rounded-full hover:bg-crimson hover:text-white transition-colors">
            <Plus size={15} /> सदस्य थप्नुहोस्
          </button>
        </div>
        {team.length === 0 ? (
          <p className="text-muted text-[13px]">कुनै टिम सदस्य थपिएको छैन।</p>
        ) : (
          <div className="flex flex-col gap-4">
            {team.map((m, i) => (
              <div key={i} className="bg-paper-soft border border-line rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-ink">सदस्य {i + 1}</h4>
                  <button type="button" aria-label={`सदस्य ${i + 1} हटाउनुहोस्`} onClick={() => removeTeamMember(i)} className="p-2 rounded-full border border-line text-ink-soft hover:text-crimson hover:border-crimson transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
                  <label className={labelCls}>
                    फोटो
                    <div className="flex flex-col items-start gap-2">
                      <div className="relative group">
                        {previews[i] || m.photo ? (
                          <>
                            <img
                              src={previews[i] || m.photo}
                              alt={m.name || 'Team member'}
                              className="w-20 h-20 rounded-full object-cover border-2 border-line bg-paper-soft"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCropState({ index: i, src: previews[i] || m.photo });
                              }}
                              title="फोटो क्रप गर्नुहोस्"
                              aria-label="फोटो क्रप गर्नुहोस्"
                              className="absolute inset-x-0 -bottom-0.5 mx-auto w-fit inline-flex items-center gap-1 bg-crimson text-white text-[10px] font-bold px-2 py-0.5 rounded-full opacity-90 hover:opacity-100 transition-opacity"
                            >
                              <Crop size={10} /> क्रप
                            </button>
                          </>
                        ) : (
                          <AuthorAvatar name={m.name || '?'} size={80} />
                        )}
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(i, e)} className={`${inputCls} py-2`} />
                    </div>
                  </label>
                  <label className={labelCls}>
                    नाम
                    <input className={inputCls} value={m.name || ''} onChange={(e) => updateTeamMember(i, 'name', e.target.value)} placeholder="सदस्यको नाम" />
                  </label>
                  <label className={labelCls}>
                    पद
                    <input className={inputCls} value={m.role || ''} onChange={(e) => updateTeamMember(i, 'role', e.target.value)} placeholder="प्रधान सम्पादक" />
                  </label>
                  <label className={labelCls}>
                    फोन
                    <input className={inputCls} value={m.phone || ''} onChange={(e) => updateTeamMember(i, 'phone', e.target.value)} placeholder="९८XXXXXXXX" />
                  </label>
                  <label className={labelCls}>
                    इमेल
                    <input className={inputCls} value={m.email || ''} onChange={(e) => updateTeamMember(i, 'email', e.target.value)} placeholder="name@example.com" />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {cropState && (
        <CircleCropModal
          imageSrc={cropState.src}
          onCancel={() => setCropState(null)}
          onApply={handleCropApply}
        />
      )}
    </div>
  );
}

export default AdminTeam;